import { useBreakpoint } from '@vivaldis/antd-ui'
import { useEffect, PropsWithChildren, useState } from 'react'
import { createMemoryHistory, Action } from 'history'
import { SideSheetsHistory } from '../typings/SideSheetsHistory'
import { SideSheetsHistoryContext } from '../context/SideSheetsHistoryContext'
import { SideSheetsElementsProvider } from './SideSheetsElementsProvider'

let sideSheetsHistoryGlobal: SideSheetsHistory | null = null

export const getSideSheetsNavigation = async (): Promise<SideSheetsHistory> => {
  return new Promise(resolve => {
    const resolveNavigationOnReady = () => {
      if (sideSheetsHistoryGlobal) {
        return resolve(sideSheetsHistoryGlobal)
      } else {
        setTimeout(resolveNavigationOnReady, 10)
      }
    }

    resolveNavigationOnReady()
  })
}

export const SideSheetsProvider = ({
  children
}: PropsWithChildren<unknown>) => {
  const breakpoint = useBreakpoint()

  const [sideSheetsHistory, setSideSheetsHistory] = useState<SideSheetsHistory>(
    () => {
      const memoryHistory = createMemoryHistory({
        initialEntries: [{ pathname: '/' }],
        initialIndex: 0
      })
      return {
        memoryHistory: memoryHistory,
        ...memoryHistory,
        entries: [],
        index: 0
      }
    }
  )

  // update global sideSheetsHistory reference
  useEffect(() => {
    sideSheetsHistoryGlobal = sideSheetsHistory

    return () => {
      sideSheetsHistoryGlobal = null
    }
  }, [sideSheetsHistory])

  useEffect(() => {
    return sideSheetsHistory.listen(() => {
      setSideSheetsHistory(prevSideSheetsHistory => {
        const base = prevSideSheetsHistory.memoryHistory

        const nextSideSheetsHistory = {
          ...base,
          ...prevSideSheetsHistory,
          index: base.index
        }

        if (base.action === Action.Pop) {
          nextSideSheetsHistory.entries = [
            ...nextSideSheetsHistory.entries.slice(0, base.index)
          ]
        } else if (base.action === Action.Push) {
          nextSideSheetsHistory.entries = [
            ...nextSideSheetsHistory.entries,
            base.location
          ]
        } else if (base.action === Action.Replace) {
          nextSideSheetsHistory.entries = [
            ...nextSideSheetsHistory.entries.slice(0, base.index - 1),
            base.location
          ]
        }
        return nextSideSheetsHistory
      })
    })
  }, [sideSheetsHistory])

  // Reset when going to mobile screen size (useful during development)
  useEffect(() => {
    if (!breakpoint.lg && sideSheetsHistory.entries.length) {
      sideSheetsHistory.go(sideSheetsHistory.entries.length * -1)
    }
  }, [breakpoint.lg, sideSheetsHistory])

  return (
    <SideSheetsHistoryContext.Provider value={sideSheetsHistory}>
      <SideSheetsElementsProvider>{children}</SideSheetsElementsProvider>
    </SideSheetsHistoryContext.Provider>
  )
}
